// Streaming LLM cost ledger → workspace/usage.json.
//
// ModelRouter emits one immutable event per actual routed model execution. This
// ledger appends those events after each ingest unit and derives every aggregate
// from the event list, so task/model/global totals cannot drift or double-count.

import * as crypto from "node:crypto";

import type {
  LlmProtocol,
  LlmTaskExecutor,
  LlmTaskId,
  LlmUsageEvent,
} from "../../llm/types.js";
import type { WorkspaceStore } from "../../infra/ingest/workspace-store.js";

export interface UsageTotals {
  calls: number;
  succeeded: number;
  failed: number;
  reused: number;
  attempts: number;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheCreationTokens: number;
  totalTokens: number;
}

export type UsageScope =
  | { kind: "episode"; episode: number }
  | { kind: "episode-range"; startEpisode: number; endEpisode: number }
  | { kind: "phase"; phase: string }
  | { kind: "run" };

export interface UsageRecord extends LlmUsageEvent {
  id: string;
  runId: string;
  scope: UsageScope;
}

export interface UsageModelIdentity {
  provider: string;
  protocol: LlmProtocol;
  model: string;
}

export interface UsageModelBreakdown extends UsageModelIdentity {
  profiles: string[];
  fingerprints: string[];
  totals: UsageTotals;
}

export interface UsageTaskSummary {
  task: LlmTaskId;
  totals: UsageTotals;
  byModel: UsageModelBreakdown[];
}

export interface UsageModelSummary extends UsageModelBreakdown {
  byTask: Array<{ task: LlmTaskId; totals: UsageTotals }>;
}

export interface EpisodeStageTiming {
  transcodeMs?: number;
  uploadMs?: number;
  generateMs?: number;
  uploadBytes?: number;
  transcoded?: boolean;
  note?: string;
}

export type UsageUnitStatus = "succeeded" | "failed" | "reused";
export type UsageRunStatus = "running" | "succeeded" | "failed" | "interrupted";

export interface EpisodeUsageRun {
  runId: string;
  status: UsageUnitStatus;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  stages: EpisodeStageTiming;
  recordIds: string[];
}

export interface EpisodeUsageSummary {
  episode: number;
  totals: UsageTotals;
  byTask: UsageTaskSummary[];
  byModel: UsageModelSummary[];
  durationMs: number;
  runs: EpisodeUsageRun[];
}

export interface UsageRun {
  id: string;
  status: UsageRunStatus;
  startedAt: string;
  endedAt: string | null;
  durationMs: number;
}

export interface UsageReport {
  version: 2;
  createdAt: string;
  updatedAt: string;
  totals: UsageTotals;
  byTask: UsageTaskSummary[];
  byModel: UsageModelSummary[];
  episodes: EpisodeUsageSummary[];
  runs: UsageRun[];
  records: UsageRecord[];
  timing: {
    totalDurationMs: number;
    modelDurationMs: number;
    episodeDurationMs: number;
  };
}

export interface UsageFlush {
  scope: UsageScope;
  status?: UsageUnitStatus;
  startedAtMs?: number;
  endedAtMs?: number;
  stages?: EpisodeStageTiming;
}

function emptyTotals(): UsageTotals {
  return {
    calls: 0,
    succeeded: 0,
    failed: 0,
    reused: 0,
    attempts: 0,
    durationMs: 0,
    inputTokens: 0,
    outputTokens: 0,
    cachedInputTokens: 0,
    cacheCreationTokens: 0,
    totalTokens: 0,
  };
}

function addEvent(total: UsageTotals, event: Pick<UsageRecord, "status" | "attempts" | "durationMs" | "inputTokens" | "outputTokens" | "cachedInputTokens" | "cacheCreationTokens">): void {
  if (event.status === "reused") {
    total.reused += 1;
    return;
  }
  total.calls += 1;
  if (event.status === "succeeded") total.succeeded += 1;
  else total.failed += 1;
  total.attempts += event.attempts;
  total.durationMs += event.durationMs;
  total.inputTokens += event.inputTokens;
  total.outputTokens += event.outputTokens;
  total.cachedInputTokens += event.cachedInputTokens;
  total.cacheCreationTokens += event.cacheCreationTokens;
  total.totalTokens = total.inputTokens + total.outputTokens;
}

function totalsOf(records: UsageRecord[]): UsageTotals {
  const totals = emptyTotals();
  for (const record of records) addEvent(totals, record);
  return totals;
}

const modelKey = (record: UsageModelIdentity): string =>
  `${record.provider}\u0000${record.protocol}\u0000${record.model}`;

function modelBreakdowns(records: UsageRecord[]): UsageModelBreakdown[] {
  const groups = new Map<string, { identity: UsageModelIdentity; profiles: Set<string>; fingerprints: Set<string>; records: UsageRecord[] }>();
  for (const record of records) {
    const key = modelKey(record);
    const group = groups.get(key) ?? {
      identity: { provider: record.provider, protocol: record.protocol, model: record.model },
      profiles: new Set<string>(),
      fingerprints: new Set<string>(),
      records: [],
    };
    group.profiles.add(record.profile);
    group.fingerprints.add(record.fingerprint);
    group.records.push(record);
    groups.set(key, group);
  }
  return [...groups.values()]
    .map((group) => ({
      ...group.identity,
      profiles: [...group.profiles].sort(),
      fingerprints: [...group.fingerprints].sort(),
      totals: totalsOf(group.records),
    }))
    .sort((left, right) => modelKey(left).localeCompare(modelKey(right)));
}

function taskSummaries(records: UsageRecord[]): UsageTaskSummary[] {
  const groups = new Map<LlmTaskId, UsageRecord[]>();
  for (const record of records) {
    const group = groups.get(record.task) ?? [];
    group.push(record);
    groups.set(record.task, group);
  }
  return [...groups.entries()]
    .map(([task, taskRecords]) => ({
      task,
      totals: totalsOf(taskRecords),
      byModel: modelBreakdowns(taskRecords),
    }))
    .sort((left, right) => left.task.localeCompare(right.task));
}

function modelSummaries(records: UsageRecord[]): UsageModelSummary[] {
  return modelBreakdowns(records).map((model) => {
    const modelRecords = records.filter((record) => modelKey(record) === modelKey(model));
    return {
      ...model,
      byTask: taskSummaries(modelRecords).map(({ task, totals }) => ({ task, totals })),
    };
  });
}

function rebuild(report: UsageReport): UsageReport {
  const updatedAt = report.updatedAt;
  const episodes = new Map<number, EpisodeUsageRun[]>();
  for (const episode of report.episodes) episodes.set(episode.episode, episode.runs);
  const episodeSummaries = [...episodes.entries()]
    .map(([episode, runs]) => {
      const ids = new Set(runs.flatMap((run) => run.recordIds));
      const records = report.records.filter((record) => ids.has(record.id));
      return {
        episode,
        totals: totalsOf(records),
        byTask: taskSummaries(records),
        byModel: modelSummaries(records),
        durationMs: runs.reduce((sum, run) => sum + run.durationMs, 0),
        runs,
      };
    })
    .sort((left, right) => left.episode - right.episode);
  const totals = totalsOf(report.records);
  return {
    ...report,
    updatedAt,
    totals,
    byTask: taskSummaries(report.records),
    byModel: modelSummaries(report.records),
    episodes: episodeSummaries,
    timing: {
      totalDurationMs: report.runs.reduce((sum, run) => sum + run.durationMs, 0),
      modelDurationMs: totals.durationMs,
      episodeDurationMs: episodeSummaries.reduce((sum, episode) => sum + episode.durationMs, 0),
    },
  };
}

function newReport(now: string): UsageReport {
  return {
    version: 2,
    createdAt: now,
    updatedAt: now,
    totals: emptyTotals(),
    byTask: [],
    byModel: [],
    episodes: [],
    runs: [],
    records: [],
    timing: { totalDurationMs: 0, modelDurationMs: 0, episodeDurationMs: 0 },
  };
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

// Owns one process run. `flush` advances an in-memory cursor over executor events;
// an event can therefore be persisted exactly once even though usage.json is
// rewritten after every episode/phase boundary.
export class UsageLedger {
  private readonly runId = crypto.randomUUID();
  private readonly startedAtMs: number;
  private cursor: number;
  private report: UsageReport;

  constructor(
    private readonly store: WorkspaceStore,
    private readonly executor: LlmTaskExecutor,
    private readonly now: () => number = () => Date.now(),
  ) {
    this.startedAtMs = this.now();
    this.cursor = executor.usageEvents().length;
    const nowIso = iso(this.startedAtMs);
    this.report = store.has("usage.json") ? store.readJson<UsageReport>("usage.json") : newReport(nowIso);
    if (this.report.version !== 2) {
      throw new Error("workspace/usage.json uses an incompatible schema; rerun ingest with --force to rebuild the workspace.");
    }
    for (const run of this.report.runs) {
      if (run.status !== "running") continue;
      run.status = "interrupted";
      run.endedAt = this.report.updatedAt;
      run.durationMs = Math.max(0, Date.parse(this.report.updatedAt) - Date.parse(run.startedAt));
    }
    this.report.runs.push({
      id: this.runId,
      status: "running",
      startedAt: nowIso,
      endedAt: null,
      durationMs: 0,
    });
    this.write(this.startedAtMs);
  }

  flush(input: UsageFlush): void {
    const endedAtMs = input.endedAtMs ?? this.now();
    const startedAtMs = input.startedAtMs ?? endedAtMs;
    const events = this.executor.usageEvents();
    const current = events.slice(this.cursor);
    const records = current.map((event) => this.toRecord(event, input.scope));
    this.cursor = events.length;
    this.report.records.push(...records);

    if (input.scope.kind === "episode") {
      const episodeNumber = input.scope.episode;
      const existing = this.report.episodes.find((episode) => episode.episode === episodeNumber);
      const runs = existing?.runs ?? [];
      runs.push({
        runId: this.runId,
        status: input.status ?? "succeeded",
        startedAt: iso(startedAtMs),
        endedAt: iso(endedAtMs),
        durationMs: Math.max(0, endedAtMs - startedAtMs),
        stages: input.stages ?? {},
        recordIds: records.map((record) => record.id),
      });
      if (!existing) {
        this.report.episodes.push({
          episode: episodeNumber,
          totals: emptyTotals(),
          byTask: [],
          byModel: [],
          durationMs: 0,
          runs,
        });
      }
    }
    this.write(endedAtMs);
  }

  finish(status: Exclude<UsageRunStatus, "running" | "interrupted">): void {
    const endedAtMs = this.now();
    if (this.executor.usageEvents().length > this.cursor) {
      this.flush({ scope: { kind: "run" }, status: status === "succeeded" ? "succeeded" : "failed", endedAtMs });
    }
    const run = this.report.runs.find((candidate) => candidate.id === this.runId);
    if (run) {
      run.status = status;
      run.endedAt = iso(endedAtMs);
      run.durationMs = Math.max(0, endedAtMs - this.startedAtMs);
    }
    this.write(endedAtMs);
  }

  private toRecord(event: LlmUsageEvent, scope: UsageScope): UsageRecord {
    return {
      ...event,
      id: `${this.runId}:${event.sequence}`,
      runId: this.runId,
      scope,
    };
  }

  private write(atMs: number): void {
    const run = this.report.runs.find((candidate) => candidate.id === this.runId);
    if (run?.status === "running") run.durationMs = Math.max(0, atMs - this.startedAtMs);
    this.report.updatedAt = iso(atMs);
    this.report = rebuild(this.report);
    this.store.writeJson("usage.json", this.report);
  }
}
